import { useQuery } from '@tanstack/react-query';
import { Suspense, useState } from 'react';
import { Link, NavLink, Outlet, ScrollRestoration } from 'react-router';
import { unreadCount } from '../../data/notifications.ts';
import { isStaff } from '../../domain/roles.ts';
import { updateNow } from '../../pwa/register.ts';
import { usePwaState } from '../../pwa/store.ts';
import { Button } from '../../ui/Button.tsx';
import {
  AccountIcon,
  ClientsIcon,
  FileTextIcon,
  OrdersIcon,
  ChevronsLeftIcon,
  PlusIcon,
  TeamIcon,
} from '../../ui/icons.tsx';
import { useNotices } from '../../ui/notices-context.ts';
import { useProfile } from '../auth/auth-context.ts';
import { ForcePasswordChange } from '../auth/ForcePasswordChange.tsx';
import { QueueRunner } from '../evidence/QueueRunner.tsx';
import { NotificationBell } from '../notifications/NotificationBell.tsx';
import { UserMenu } from './UserMenu.tsx';

// Layout autenticado (§12). Móvil: barra superior con marca y campana, navegación inferior por rol.
// Escritorio (≥ 900 px): sidebar claro de altura completa con marca y navegación; la barra
// superior queda sobre el contenido con la campana y el menú de usuario (desplegables).
const COLLAPSED_KEY = 'miyama:sidebar-collapsed';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function Shell() {
  const profile = useProfile();
  const [collapsed, setCollapsed] = useState(readCollapsed);

  function toggleCollapsed() {
    setCollapsed((current) => {
      try {
        localStorage.setItem(COLLAPSED_KEY, current ? '0' : '1');
      } catch {
        // Sin almacenamiento: el menú funciona igual, solo no recuerda el estado.
      }
      return !current;
    });
  }

  const unread = useQuery({
    queryKey: ['unread'],
    queryFn: unreadCount,
    refetchInterval: 60_000,
    enabled: !profile.must_change_password,
  });

  if (profile.must_change_password) {
    return (
      <div className="shell shell-bare">
        <header className="topbar">
          <Brand />
        </header>
        <main className="main">
          <ForcePasswordChange />
        </main>
      </div>
    );
  }

  const items = [
    { to: '/', label: 'Órdenes', icon: <OrdersIcon />, end: true, sidebarOnly: false },
    ...(isStaff(profile.role)
      ? [
          { to: '/new', label: 'Nueva orden', icon: <PlusIcon />, end: false, sidebarOnly: true },
          { to: '/clients', label: 'Clientes', icon: <ClientsIcon />, end: false, sidebarOnly: false },
          // Solo en el menú de escritorio: en el teléfono cada presupuesto se abre desde su orden.
          { to: '/quotes', label: 'Presupuestos', icon: <FileTextIcon />, end: false, sidebarOnly: true },
        ]
      : []),
    ...(isStaff(profile.role)
      ? [{ to: '/team', label: 'Equipo', icon: <TeamIcon />, end: false, sidebarOnly: false }]
      : []),
    { to: '/account', label: 'Cuenta', icon: <AccountIcon />, end: false, sidebarOnly: false },
  ];
  const count = unread.data ?? 0;

  return (
    <div className={`shell${collapsed ? ' shell-collapsed' : ''}`}>
      <aside className="sidebar">
        <div className="sidebar-brand">
          <Brand to="/" />
        </div>
        <nav className="nav" aria-label="Navegación principal">
          <ul>
            {items.map((item) => (
              <li key={item.to} className={item.sidebarOnly ? 'nav-sidebar-only' : undefined}>
                <NavLink to={item.to} end={item.end} className="nav-link" title={item.label}>
                  <span className="nav-icon">{item.icon}</span>
                  <span className="nav-label">{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <div className="sidebar-footer">
          <button
            type="button"
            className="sidebar-toggle"
            onClick={toggleCollapsed}
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Expandir menú lateral' : 'Contraer menú lateral'}
            title={collapsed ? 'Expandir menú' : 'Contraer menú'}
          >
            <span className="nav-icon">
              <ChevronsLeftIcon />
            </span>
            <span className="sidebar-toggle-label">Contraer menú</span>
          </button>
        </div>
      </aside>
      <div className="content">
        <header className="topbar">
          <div className="topbar-brand">
            <Brand to="/" />
          </div>
          <div className="topbar-end">
            <NotificationBell count={count} />
            <span className="topbar-user" aria-label="Usuario actual">
              {profile.name}
            </span>
            <UserMenu />
          </div>
        </header>
        <main className="main">
          <StatusBanners />
          <Suspense fallback={<p aria-busy="true">Cargando…</p>}>
            <Outlet />
          </Suspense>
        </main>
        {/* Cada pantalla nueva empieza arriba; atrás/adelante recuperan la posición guardada. */}
        <ScrollRestoration />
      </div>
      <QueueRunner userId={profile.id} />
    </div>
  );
}

function StatusBanners() {
  const { online, updateAvailable, busyKeys } = usePwaState();
  const notices = useNotices();
  const busy = busyKeys.size > 0;
  return (
    <>
      {!online ? (
        <p className="banner banner-warning page-banner" role="status">
          Sin conexión. Puedes revisar lo que ya está abierto, escribir borradores y preparar adjuntos; se guardarán y
          subirán al volver la señal.
        </p>
      ) : null}
      {updateAvailable ? (
        <div className="banner banner-info page-banner row row-between" role="status">
          <span>
            Hay una nueva versión disponible.
            {busy ? ' Se podrá actualizar cuando termines de guardar o subir lo pendiente.' : ''}
          </span>
          <Button
            variant="secondary"
            disabled={busy}
            onClick={() =>
              void updateNow().then((ok) => {
                if (!ok) notices.error('Termina de guardar o subir lo pendiente antes de actualizar.');
              })
            }
          >
            Actualizar
          </Button>
        </div>
      ) : null}
    </>
  );
}

// Con `to`, la marca es un enlace (al inicio); sin él, solo se muestra. En el sidebar,
// "Operaciones" va en una segunda línea más pequeña; en la barra superior, en la misma línea.
function Brand({ to }: { to?: string }) {
  const content = (
    <>
      <span className="brand-mark">
        <img src="/logo.png" alt="" />
      </span>
      <span className="brand-name">
        Miyama <span className="brand-sub">Operaciones</span>
      </span>
    </>
  );
  return to ? (
    <Link to={to} className="brand brand-link">
      {content}
    </Link>
  ) : (
    <div className="brand">{content}</div>
  );
}
